import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useIntl } from 'react-intl';
import { Input, Label, ButtonPrimary, ProgressCircle } from '@americanexpress/dls-react';
import {
  formatPostcodeInput,
  formatSSNInput,
  searchValidationSchema,
} from './validations';
import { setSearchResults, setUserSession, setNoResults, setApplicantName, setSessionExpiredBanner } from '../../../duck';
import { useSearchStatusRequest } from '../../../utils/APIs';

const initialFormState = {
  ssn: '',
  postcode: '',
};

const SearchPage = () => {
  const dispatch = useDispatch();
  const intl = useIntl();

  const [serviceError, setServiceError] = useState(null);

  const ssnLabel = intl.formatMessage({ id: 'search.page.ssn' });
  const postcodeLabel = intl.formatMessage({ id: 'search.page.postcode' });

  const [formValues, setFormValues] = useState(initialFormState);
  const [errors, setErrors] = useState(initialFormState);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    runSearchStatusRequest,
  } = useSearchStatusRequest(formValues);

  const handleInputChange = (field, formatter) => (event) => {
    const formattedValue = formatter(event.target.value);

    setFormValues((previous) => ({
      ...previous,
      [field]: formattedValue,
    }));

    if (errors[field]) {
      setErrors((previous) => ({
        ...previous,
        [field]: '',
      }));
    }

    setServiceError(null);
  };

  // eslint-disable-next-line complexity -- complexity is high due to multiple branches of logic for handling search results and errors
  const handleSubmit = async (event) => {
    event.preventDefault();

    try {
      await searchValidationSchema.validate(formValues, { abortEarly: false });
      setErrors(initialFormState);
      dispatch(setSessionExpiredBanner(false));
      setIsSubmitting(true);

      const { data, error } = await runSearchStatusRequest();
      const applications = data?.body?.applications;
      const userSessionToken =
        data?.body?.user_session_token || data?.user_session_token || null;
      const hasResults = !error && Array.isArray(applications) && applications.length > 0;
      const hasNoResults =
        (!error && Array.isArray(applications) && applications.length === 0) ||
        data?.status === 204;

      if (!error && userSessionToken) {
        dispatch(setUserSession(userSessionToken));
      }

      if (hasResults) {
        dispatch(setApplicantName(data?.body?.user_first_name));
        dispatch(setSearchResults(applications));
        setServiceError(null);
      } else if (hasNoResults) {
        dispatch(setNoResults(true));
      } else {
        setServiceError({
          isNoResults: false,
          response: data,
          errorMessage: intl.formatMessage({
            id: 'search.page.service.error',
          }),
        });
      }
      setFormValues(initialFormState);
    } catch (error) {
      if (error.name !== 'ValidationError') {
        return;
      }

      const validationErrors = error.inner.reduce(
        (accumulator, validationError) => ({
          ...accumulator,
          [validationError.path]: validationError.message,
        }),
        initialFormState,
      );

      setErrors(validationErrors);
    }
    setIsSubmitting(false);
  };

  return (
    <form onSubmit={handleSubmit} noValidate={true}>
      <div className="pad-3-b">
        <p>{intl.formatMessage({ id: 'search.page.description' })}</p>
      </div>
      <div className="pad-3-b row">
        <div className="col-md-3">
          <Label htmlFor="search-input-ssn">{ssnLabel}</Label>
          <Input
            id="search-input-ssn"
            label={ssnLabel}
            value={formValues.ssn}
            disabled={isSubmitting}
            onChange={handleInputChange('ssn', formatSSNInput)}
            onPaste={(e) => e.preventDefault()}
            type="password"
            inputMode="numeric"
            maxLength={9}
            aria-invalid={Boolean(errors.ssn)}
          />
          {errors.ssn ? (
            <p className="mar-1-t dls-color-warning" role="alert">
              {errors.ssn}
            </p>
          ) : null}
        </div>
        <div className="col-md-3">
          <Label htmlFor="search-input-postcode">{postcodeLabel}</Label>
          <Input
            id="search-input-postcode"
            label={postcodeLabel}
            value={formValues.postcode}
            onChange={handleInputChange('postcode', formatPostcodeInput)}
            disabled={isSubmitting}
            inputMode="numeric"
            warning={true}
            maxLength={5}
            aria-invalid={Boolean(errors.postcode)}
          />
          {errors.postcode ? (
            <p className="mar-1-t dls-color-warning" role="alert">
              {errors.postcode}
            </p>
          ) : null}
        </div>
        <div className="col-md-3 pad-4-t">
          <ButtonPrimary type="submit" size="sm" disabled={isSubmitting}>
            {intl.formatMessage({ id: 'search.page.submit' })}
          </ButtonPrimary>
        </div>
      </div>
      {isSubmitting && (
        <div className="pad-2-t" style={{ textAlign: 'center' }}>
          <ProgressCircle
            determinate={false}
            label={intl.formatMessage({
              id: 'search.page.loading',
              defaultMessage: 'Searching for your application. Please wait.',
            })}
          />
        </div>
      )}
      {serviceError ? (
        <p
          className="mar-1-t dls-color-warning"
          role="alert"
        >
          {serviceError.errorMessage}
        </p>
      ) : null}
    </form>
  );
};

export default SearchPage;
